import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { Wine, Users, Calendar, MapPin, Loader2, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { eventApi, tastingApi } from '@/api/services';
import { useEventStore } from '@/stores/eventStore';
import { useTastingStore } from '@/stores/tastingStore';
import { useTheme } from '@/theme/ThemeProvider';
import TastingLayout from '@/components/layout/TastingLayout';
import WaitingRoom from '@/components/guest/WaitingRoom';

interface JoinFormData {
  guestName: string;
  email?: string;
}

export default function JoinPage() {
  const { accessCode } = useParams<{ accessCode: string }>();
  const navigate = useNavigate();
  const { applyBranding } = useTheme();
  const { setEvent, setGuest, setError, guest: existingGuest } = useEventStore();
  const { setSession, setSpirits, reset: resetTasting, session } = useTastingStore();
  const [isJoining, setIsJoining] = useState(false);
  const [showWaitingRoom, setShowWaitingRoom] = useState(false);
  const [joinedGuest, setJoinedGuest] = useState(existingGuest);

  const { register, handleSubmit, formState: { errors } } = useForm<JoinFormData>();

  // Fetch event data
  const { data: event, isLoading, error } = useQuery({
    queryKey: ['event', accessCode],
    queryFn: () => eventApi.getByAccessCode(accessCode!),
    enabled: !!accessCode,
    retry: false,
  });

  // Check if we should show waiting room or redirect to session
  useEffect(() => {
    if (existingGuest && event) {
      if (event.status === 'active' && session) {
        // Event is active and we have a session, go to tasting
        navigate(`/session/${session.uuid}`);
      } else if (event.status === 'scheduled') {
        // Event not started, show waiting room
        setShowWaitingRoom(true);
        setJoinedGuest(existingGuest);
      }
    }
  }, [existingGuest, event, session, navigate]);

  // Apply branding when event loads
  useEffect(() => {
    if (event?.branding) {
      applyBranding(event.branding);
    }
    if (event) {
      setEvent(event);
    }
  }, [event, applyBranding, setEvent]);

  // Handle join
  const joinMutation = useMutation({
    mutationFn: async (data: JoinFormData) => {
      // Join event
      const guest = await eventApi.joinByCode(accessCode!, data.guestName, data.email);

      // Start tasting session
      const session = await tastingApi.startSession({
        venue_id: 0, // Will be set by backend from event
        tasting_type: 'event',
        guest_name: data.guestName,
        event_id: guest.event_id,
        guest_id: guest.id,
      });

      return { guest, session };
    },
    onSuccess: ({ guest, session }) => {
      setGuest(guest);
      setSession(session);
      setJoinedGuest(guest);
      resetTasting();

      // Add spirits from event to session
      if (event?.spirits) {
        event.spirits.forEach(async (spirit) => {
          await tastingApi.addSpirit(session.uuid, {
            spirit_name: spirit.spirit_name,
            spirit_brand: spirit.spirit_brand,
          });
        });
      }

      // If event is not yet active, show waiting room
      if (event?.status === 'scheduled') {
        setShowWaitingRoom(true);
        toast.success('Du bist dabei! Warte auf den Start des Events.');
      } else {
        toast.success('Willkommen beim Tasting!');
        navigate(`/session/${session.uuid}`);
      }
    },
    onError: (error: Error) => {
      toast.error(error.message);
      setError(error.message);
    },
  });

  const onSubmit = async (data: JoinFormData) => {
    setIsJoining(true);
    try {
      await joinMutation.mutateAsync(data);
    } finally {
      setIsJoining(false);
    }
  };

  if (isLoading) {
    return (
      <TastingLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
        </div>
      </TastingLayout>
    );
  }

  if (error || !event) {
    return (
      <TastingLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
          <AlertCircle className="w-16 h-16 text-danger-500 mb-4" />
          <h1 className="text-2xl font-display font-bold text-dark-100 mb-2">
            Event nicht gefunden
          </h1>
          <p className="text-dark-400 max-w-md">
            Der Event-Code "{accessCode}" ist ungültig oder das Event existiert nicht mehr.
          </p>
        </div>
      </TastingLayout>
    );
  }

  const isEventJoinable = event.status === 'scheduled' || event.status === 'active';
  const isFull = event.current_participants >= event.max_participants;

  // Show waiting room if joined but event not started
  if (showWaitingRoom && joinedGuest && event.status === 'scheduled') {
    return (
      <TastingLayout showBranding branding={event.branding}>
        <WaitingRoom
          event={event}
          guest={joinedGuest}
          onEventStart={() => {
            if (session) {
              navigate(`/session/${session.uuid}`);
            }
          }}
        />
      </TastingLayout>
    );
  }

  return (
    <TastingLayout showBranding branding={event.branding}>
      <div className="max-w-lg mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          {/* Event Cover Image */}
          {event.branding?.cover_image_url && (
            <div className="mb-6 rounded-2xl overflow-hidden">
              <img
                src={event.branding.cover_image_url}
                alt={event.name}
                className="w-full h-48 object-cover"
              />
            </div>
          )}

          {/* Event Title */}
          <h1 className="text-3xl md:text-4xl font-display font-bold text-dark-50 mb-4">
            {event.name}
          </h1>

          {event.description && (
            <p className="text-dark-300 mb-6">{event.description}</p>
          )}

          {/* Event Meta */}
          <div className="flex flex-wrap justify-center gap-4 text-sm text-dark-400 mb-8">
            {event.venue_name && (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>{event.venue_name}</span>
              </div>
            )}
            {event.scheduled_at && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{format(new Date(event.scheduled_at), 'PPP', { locale: de })}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span>{event.current_participants} / {event.max_participants} Teilnehmer</span>
            </div>
            {event.spirits && event.spirits.length > 0 && (
              <div className="flex items-center gap-2">
                <Wine className="w-4 h-4" />
                <span>{event.spirits.length} Spirituosen</span>
              </div>
            )}
          </div>
        </motion.div>

        {/* Spirits Preview */}
        {event.spirits && event.spirits.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card mb-8"
          >
            <h2 className="text-lg font-semibold text-dark-100 mb-4">Heutige Verkostung</h2>
            <div className="space-y-3">
              {event.spirits.map((spirit, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 bg-dark-800/50 rounded-xl"
                >
                  <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-400 font-medium">
                    {index + 1}
                  </div>
                  <div>
                    <div className="font-medium text-dark-100">{spirit.spirit_name}</div>
                    {spirit.spirit_brand && (
                      <div className="text-sm text-dark-400">{spirit.spirit_brand}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Join Form */}
        {isEventJoinable && !isFull ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card"
          >
            <h2 className="text-lg font-semibold text-dark-100 mb-4">Teilnehmen</h2>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="label">Dein Name *</label>
                <input
                  type="text"
                  {...register('guestName', {
                    required: 'Bitte gib deinen Namen ein',
                    minLength: { value: 2, message: 'Name muss mindestens 2 Zeichen haben' },
                  })}
                  placeholder="Max Mustermann"
                  className="input"
                  disabled={isJoining}
                />
                {errors.guestName && (
                  <p className="text-danger-400 text-sm mt-1">{errors.guestName.message}</p>
                )}
              </div>

              <div>
                <label className="label">E-Mail (optional)</label>
                <input
                  type="email"
                  {...register('email', {
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Ungültige E-Mail-Adresse',
                    },
                  })}
                  placeholder="max@beispiel.de"
                  className="input"
                  disabled={isJoining}
                />
                {errors.email && (
                  <p className="text-danger-400 text-sm mt-1">{errors.email.message}</p>
                )}
                <p className="text-dark-500 text-xs mt-1">
                  Für personalisierte Ergebnisse und Empfehlungen
                </p>
              </div>

              <button
                type="submit"
                disabled={isJoining}
                className="btn-primary w-full btn-lg"
              >
                {isJoining ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Beitritt...
                  </>
                ) : (
                  'Jetzt teilnehmen'
                )}
              </button>
            </form>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card text-center"
          >
            {isFull ? (
              <>
                <AlertCircle className="w-12 h-12 text-warning-500 mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-dark-100 mb-2">Event ist voll</h2>
                <p className="text-dark-400">
                  Leider ist die maximale Teilnehmerzahl bereits erreicht.
                </p>
              </>
            ) : (
              <>
                <AlertCircle className="w-12 h-12 text-dark-500 mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-dark-100 mb-2">
                  {event.status === 'completed' ? 'Event beendet' : 'Event nicht verfügbar'}
                </h2>
                <p className="text-dark-400">
                  {event.status === 'completed'
                    ? 'Dieses Event wurde bereits abgeschlossen.'
                    : 'Dieses Event ist derzeit nicht für Beitritte verfügbar.'}
                </p>
              </>
            )}
          </motion.div>
        )}

        {/* Intro Text */}
        {event.branding?.intro_text && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-8 text-center text-dark-400 text-sm"
          >
            {event.branding.intro_text}
          </motion.div>
        )}
      </div>
    </TastingLayout>
  );
}
